import { useMemo, useState } from "react";
import * as DocumentPicker from "expo-document-picker";
import { useRouter } from "expo-router";
import { Alert, Pressable, ScrollView, StyleSheet, View } from "react-native";

import {
  AppText,
  Button,
  ButtonText,
  Card,
  ProtectedScreen,
  Row,
  SearchableSelect,
  Stack,
  TextField,
} from "~/components/ui";
import { useWorkspaceReadOnly } from "~/hooks/useWorkspaceReadOnly";
import {
  usePrimaryWorkspace,
  useWorkspaceClients,
  useWorkspaceProjects,
  useWorkspaceTasks,
  useWorkspaceTimeEntries,
} from "~/hooks/workspace";
import {
  fonts,
  palette,
  radii,
  spacing,
  useTheme,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import { parseIcsEvents, type ParsedCalendarEvent } from "~/utils/ics";

const GRID_START_HOUR = 7;
const GRID_END_HOUR = 19;
const HOUR_HEIGHT = 56;
const DAY_COLUMN_MIN_WIDTH = 112;
const BILLABLE_OPTIONS = [
  { label: "Billable", value: "true" },
  { label: "Non-billable", value: "false" },
];

function startOfWeekMonday(date: Date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  const day = next.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  next.setDate(next.getDate() + offset);
  return next;
}

function addDays(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function sameDay(left: Date, right: Date) {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function formatWeekLabel(weekStart: Date) {
  const weekEnd = addDays(weekStart, 6);
  const startLabel = weekStart.toLocaleDateString([], {
    day: "numeric",
    month: "short",
  });
  const endLabel = weekEnd.toLocaleDateString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return `${startLabel} – ${endLabel}`;
}

function hourLabels() {
  const hours: number[] = [];
  for (let hour = GRID_START_HOUR; hour < GRID_END_HOUR; hour += 1) {
    hours.push(hour);
  }
  return hours;
}

function clampToGrid(date: Date, day: Date) {
  const startBound = new Date(day);
  startBound.setHours(GRID_START_HOUR, 0, 0, 0);
  const endBound = new Date(day);
  endBound.setHours(GRID_END_HOUR, 0, 0, 0);
  const startMs = Math.max(date.getTime(), startBound.getTime());
  return { startBound, endBound, startMs };
}

export default function CalendarScreen() {
  const { colors } = useTheme();
  const stylesheet = useThemedStyles(makeStyles);
  const router = useRouter();
  const workspace = usePrimaryWorkspace();
  const { readOnly } = useWorkspaceReadOnly(workspace.data?.id);
  const clients = useWorkspaceClients(workspace.data?.id);
  const projects = useWorkspaceProjects(workspace.data?.id);
  const tasks = useWorkspaceTasks(workspace.data?.id);
  const timeEntries = useWorkspaceTimeEntries(workspace.data?.id);

  const [weekAnchor, setWeekAnchor] = useState(() =>
    startOfWeekMonday(new Date()),
  );
  const [icsText, setIcsText] = useState("");
  const [parsedEvents, setParsedEvents] = useState<ParsedCalendarEvent[]>([]);
  const [draftStart, setDraftStart] = useState<Date | null>(null);
  const [description, setDescription] = useState("");
  const [clientId, setClientId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [taskId, setTaskId] = useState("");
  const [minutes, setMinutes] = useState("60");
  const [billable, setBillable] = useState("true");
  const [hourlyRate, setHourlyRate] = useState("");
  const [saving, setSaving] = useState(false);

  const weekStart = weekAnchor;
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)),
    [weekStart],
  );
  const hours = useMemo(() => hourLabels(), []);
  const gridHeight = hours.length * HOUR_HEIGHT;

  const clientRecords = clients.data ?? [];
  const projectRecords = projects.data ?? [];
  const taskRecords = tasks.data ?? [];
  const clientOptions = [
    { label: "No client", value: "" },
    ...clientRecords.map((client) => ({
      label: client.name,
      value: client.id,
    })),
  ];
  const projectOptions = [
    { label: "No project", value: "" },
    ...projectRecords
      .filter((project) => !clientId || project.client_id === clientId)
      .map((project) => ({ label: project.name, value: project.id })),
  ];
  const projectById = useMemo(
    () => new Map(projectRecords.map((project) => [project.id, project])),
    [projectRecords],
  );
  const taskOptions = [
    { label: "No task", value: "" },
    ...taskRecords
      .filter((task) => {
        if (projectId) return task.project_id === projectId;
        if (!clientId) return true;
        const project = task.project_id
          ? projectById.get(task.project_id)
          : null;
        return project?.client_id === clientId;
      })
      .map((task) => ({ label: task.name, value: task.id })),
  ];

  const closedEntries = useMemo(
    () =>
      (timeEntries.data ?? []).filter(
        (entry) => entry.ended_at !== null && entry.duration_seconds > 0,
      ),
    [timeEntries.data],
  );

  const entriesByDay = useMemo(() => {
    return days.map((day) => {
      const dayEntries = closedEntries.filter((entry) =>
        sameDay(new Date(entry.started_at), day),
      );
      return dayEntries
        .map((entry) => {
          const startedAt = new Date(entry.started_at);
          const endedAt = new Date(entry.ended_at!);
          const { startBound, endBound, startMs } = clampToGrid(startedAt, day);
          const endMs = Math.min(endedAt.getTime(), endBound.getTime());
          if (endMs <= startBound.getTime() || startMs >= endBound.getTime()) {
            return null;
          }
          const top =
            ((startMs - startBound.getTime()) /
              (endBound.getTime() - startBound.getTime())) *
            gridHeight;
          const height = Math.max(
            22,
            ((endMs - startMs) / (endBound.getTime() - startBound.getTime())) *
              gridHeight,
          );
          return { entry, top, height };
        })
        .filter((value): value is NonNullable<typeof value> => value !== null);
    });
  }, [closedEntries, days, gridHeight]);

  function openSlot(day: Date, hour: number) {
    if (readOnly) return;
    const start = new Date(day);
    start.setHours(hour, 0, 0, 0);
    setDraftStart(start);
    setDescription("");
    setMinutes("60");
    setBillable("true");
    setHourlyRate("");
    setClientId("");
    setProjectId("");
    setTaskId("");
  }

  function resetDraft() {
    setDraftStart(null);
    setDescription("");
    setMinutes("60");
    setBillable("true");
    setHourlyRate("");
    setClientId("");
    setProjectId("");
    setTaskId("");
  }

  async function saveDraft() {
    if (!draftStart || !description.trim()) return;
    setSaving(true);
    try {
      await timeEntries.create.mutateAsync({
        description: description.trim(),
        clientId,
        projectId,
        taskId,
        minutes: String(Math.max(1, Math.round(Number(minutes) || 60))),
        billable,
        hourlyRate,
        startedAt: draftStart.toISOString(),
      });
      resetDraft();
    } catch (error) {
      Alert.alert(
        "Could not create entry",
        error instanceof Error ? error.message : "Unable to save time entry.",
      );
    } finally {
      setSaving(false);
    }
  }

  function applyIcsText(raw: string) {
    const events = parseIcsEvents(raw);
    setParsedEvents(events);
    if (!events.length) {
      Alert.alert("No events found", "Paste a valid ICS calendar export.");
    }
  }

  async function pickIcsFile() {
    const result = await DocumentPicker.getDocumentAsync({
      type: [
        "text/calendar",
        "application/ics",
        "text/plain",
        "application/octet-stream",
      ],
      copyToCacheDirectory: true,
    });
    if (result.canceled) return;
    const asset = result.assets[0];
    if (!asset) return;
    try {
      const text = asset.file
        ? await asset.file.text()
        : await (await fetch(asset.uri)).text();
      setIcsText(text);
      applyIcsText(text);
    } catch (error) {
      Alert.alert(
        "Could not read file",
        error instanceof Error ? error.message : "Unable to read ICS file.",
      );
    }
  }

  async function addEventAsTime(event: ParsedCalendarEvent) {
    if (readOnly) return;
    try {
      await timeEntries.createFromRange.mutateAsync({
        description: event.title,
        startedAt: event.startsAt.toISOString(),
        endedAt: event.endsAt.toISOString(),
        billable: true,
      });
      Alert.alert("Time entry added", event.title);
    } catch (error) {
      Alert.alert(
        "Could not add event",
        error instanceof Error ? error.message : "Unable to create time entry.",
      );
    }
  }

  return (
    <ProtectedScreen
      title="Calendar"
      subtitle="Week view of closed time entries. Tap a slot to log time, or import events from an ICS calendar."
    >
      <Card gap="md">
        <Row between wrap>
          <Button
            compact
            variant="outline"
            onPress={() => setWeekAnchor(addDays(weekStart, -7))}
          >
            <ButtonText>Previous week</ButtonText>
          </Button>
          <AppText weight="semibold">{formatWeekLabel(weekStart)}</AppText>
          <Row gap="sm" wrap>
            <Button
              compact
              variant="outline"
              onPress={() => setWeekAnchor(startOfWeekMonday(new Date()))}
            >
              <ButtonText>This week</ButtonText>
            </Button>
            <Button
              compact
              variant="outline"
              onPress={() => setWeekAnchor(addDays(weekStart, 7))}
            >
              <ButtonText>Next week</ButtonText>
            </Button>
          </Row>
        </Row>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator
          contentContainerStyle={stylesheet.weekScroll}
        >
          <View style={stylesheet.weekFrame}>
            <View style={stylesheet.timeGutter}>
              <View style={stylesheet.dayHeaderSpacer} />
              {hours.map((hour) => (
                <View key={hour} style={stylesheet.hourLabelCell}>
                  <AppText size="xs" style={stylesheet.muted}>
                    {`${String(hour).padStart(2, "0")}:00`}
                  </AppText>
                </View>
              ))}
            </View>

            {days.map((day, dayIndex) => (
              <View key={day.toISOString()} style={stylesheet.dayColumn}>
                <View style={stylesheet.dayHeader}>
                  <AppText size="xs" weight="semibold">
                    {day.toLocaleDateString([], { weekday: "short" })}
                  </AppText>
                  <AppText size="xs" style={stylesheet.muted}>
                    {day.toLocaleDateString([], {
                      day: "numeric",
                      month: "short",
                    })}
                  </AppText>
                </View>
                <View style={[stylesheet.dayGrid, { height: gridHeight }]}>
                  {hours.map((hour) => (
                    <Pressable
                      key={`${dayIndex}-${hour}`}
                      accessibilityRole="button"
                      accessibilityLabel={`Add time at ${hour}:00`}
                      style={stylesheet.hourSlot}
                      onPress={() => openSlot(day, hour)}
                    />
                  ))}
                  {entriesByDay[dayIndex]?.map(({ entry, top, height }) => (
                    <Pressable
                      key={entry.id}
                      accessibilityRole="button"
                      accessibilityLabel={entry.description ?? "Time entry"}
                      style={[
                        stylesheet.entryBlock,
                        {
                          top,
                          height,
                          backgroundColor: entry.billable
                            ? palette.brandForest
                            : colors.mutedForeground,
                        },
                      ]}
                      onPress={() =>
                        router.push(`/time-entries/${entry.id}` as never)
                      }
                    >
                      <AppText
                        size="xs"
                        weight="semibold"
                        style={stylesheet.entryText}
                        numberOfLines={2}
                      >
                        {entry.description ?? "Time"}
                      </AppText>
                    </Pressable>
                  ))}
                </View>
              </View>
            ))}
          </View>
        </ScrollView>

        {timeEntries.isLoading ? (
          <AppText size="xs">Loading time entries…</AppText>
        ) : null}
        {timeEntries.error instanceof Error ? (
          <AppText size="xs" tone="danger">
            {timeEntries.error.message}
          </AppText>
        ) : null}
      </Card>

      {draftStart && !readOnly ? (
        <Card gap="md">
          <Stack gap="xs">
            <AppText weight="semibold">New time entry</AppText>
            <AppText size="xs">
              Starting{" "}
              {draftStart.toLocaleString([], {
                weekday: "short",
                day: "numeric",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </AppText>
          </Stack>
          <View style={stylesheet.formGrid}>
            <View style={stylesheet.fieldWide}>
              <AppText size="xs" weight="semibold">
                Description
              </AppText>
              <TextField
                value={description}
                placeholder="Client call, design pass…"
                accessibilityLabel="Calendar entry description"
                onChangeText={setDescription}
              />
            </View>
            <View style={stylesheet.field}>
              <AppText size="xs" weight="semibold">
                Minutes
              </AppText>
              <TextField
                value={minutes}
                placeholder="60"
                keyboardType="numeric"
                accessibilityLabel="Calendar entry minutes"
                onChangeText={setMinutes}
              />
            </View>
            <View style={stylesheet.field}>
              <AppText size="xs" weight="semibold">
                Billing
              </AppText>
              <SearchableSelect
                title="Choose billing state"
                value={billable}
                options={BILLABLE_OPTIONS}
                onValueChange={setBillable}
              />
            </View>
            <View style={stylesheet.field}>
              <AppText size="xs" weight="semibold">
                Client
              </AppText>
              <SearchableSelect
                title="Choose client"
                value={clientId}
                options={clientOptions}
                onValueChange={(value) => {
                  setClientId(value);
                  setProjectId("");
                  setTaskId("");
                }}
              />
            </View>
            <View style={stylesheet.field}>
              <AppText size="xs" weight="semibold">
                Project
              </AppText>
              <SearchableSelect
                title="Choose project"
                value={projectId}
                options={projectOptions}
                onValueChange={(value) => {
                  setProjectId(value);
                  setTaskId("");
                }}
              />
            </View>
            <View style={stylesheet.field}>
              <AppText size="xs" weight="semibold">
                Task
              </AppText>
              <SearchableSelect
                title="Choose task"
                value={taskId}
                options={taskOptions}
                onValueChange={setTaskId}
              />
            </View>
            <View style={stylesheet.field}>
              <AppText size="xs" weight="semibold">
                Hourly rate (optional)
              </AppText>
              <TextField
                value={hourlyRate}
                placeholder="Use matching preset"
                keyboardType="numeric"
                onChangeText={setHourlyRate}
              />
            </View>
          </View>
          <Row gap="sm" wrap>
            <Button
              disabled={!description.trim() || saving}
              onPress={() => void saveDraft()}
            >
              <ButtonText>{saving ? "Saving…" : "Save entry"}</ButtonText>
            </Button>
            <Button variant="outline" onPress={resetDraft}>
              <ButtonText>Cancel</ButtonText>
            </Button>
          </Row>
        </Card>
      ) : null}

      <Card gap="md">
        <Stack gap="xs">
          <AppText weight="semibold">Import calendar events</AppText>
          <AppText size="xs">
            Paste an ICS export or pick a `.ics` file, then add events as manual
            time entries.
          </AppText>
        </Stack>
        <TextField
          value={icsText}
          placeholder="BEGIN:VCALENDAR…"
          accessibilityLabel="ICS calendar paste"
          multiline
          style={stylesheet.icsField}
          onChangeText={setIcsText}
        />
        <Row gap="sm" wrap>
          <Button
            compact
            variant="outline"
            onPress={() => applyIcsText(icsText)}
          >
            <ButtonText>Parse paste</ButtonText>
          </Button>
          <Button compact variant="outline" onPress={() => void pickIcsFile()}>
            <ButtonText>Pick .ics file</ButtonText>
          </Button>
        </Row>
        {parsedEvents.length === 0 ? (
          <AppText size="xs" style={stylesheet.muted}>
            No imported events yet.
          </AppText>
        ) : (
          <Stack gap="sm">
            {parsedEvents.map((event) => (
              <View key={event.uid} style={stylesheet.eventRow}>
                <Stack gap="xs" style={stylesheet.eventCopy}>
                  <AppText weight="semibold">{event.title}</AppText>
                  <AppText size="xs" style={stylesheet.muted}>
                    {event.startsAt.toLocaleString()} →{" "}
                    {event.endsAt.toLocaleString()}
                  </AppText>
                </Stack>
                <Button
                  compact
                  disabled={readOnly || timeEntries.createFromRange.isPending}
                  onPress={() => void addEventAsTime(event)}
                >
                  <ButtonText>Add as time</ButtonText>
                </Button>
              </View>
            ))}
          </Stack>
        )}
      </Card>
    </ProtectedScreen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    muted: { color: colors.mutedForeground },
    weekScroll: { flexGrow: 1 },
    weekFrame: {
      flexGrow: 1,
      flexDirection: "row",
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      backgroundColor: colors.background,
      overflow: "hidden",
    },
    timeGutter: {
      width: 56,
      borderRightWidth: 1,
      borderRightColor: colors.border,
      backgroundColor: colors.surface,
    },
    dayHeaderSpacer: {
      height: 52,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    hourLabelCell: {
      height: HOUR_HEIGHT,
      justifyContent: "flex-start",
      paddingTop: spacing.xs,
      paddingHorizontal: spacing.xs,
    },
    dayColumn: {
      minWidth: DAY_COLUMN_MIN_WIDTH,
      flexGrow: 1,
      borderRightWidth: 1,
      borderRightColor: colors.border,
    },
    dayHeader: {
      height: 52,
      gap: 2,
      justifyContent: "center",
      paddingHorizontal: spacing.s,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      backgroundColor: colors.surface,
    },
    dayGrid: {
      position: "relative",
    },
    hourSlot: {
      height: HOUR_HEIGHT,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
    },
    entryBlock: {
      position: "absolute",
      left: 4,
      right: 4,
      borderRadius: radii.s,
      paddingHorizontal: spacing.xs,
      paddingVertical: 4,
      overflow: "hidden",
    },
    entryText: {
      color: palette.brandSand,
      fontFamily: fonts.medium,
    },
    formGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    field: {
      minWidth: 180,
      flexGrow: 1,
      gap: spacing.s,
    },
    fieldWide: {
      width: "100%",
      gap: spacing.s,
    },
    icsField: {
      minHeight: 120,
      textAlignVertical: "top",
      fontFamily: fonts.mono,
    },
    eventRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.m,
      paddingVertical: spacing.s,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: colors.border,
    },
    eventCopy: {
      flexGrow: 1,
      flexShrink: 1,
      minWidth: 200,
    },
  });
